"use client"

import { forwardRef } from "react"
import type { ElementType, CSSProperties } from "react"
import { useSidebarContext } from "./hooks/useSidebarContext"
import type { SidebarSafeAreaProps } from "./Sidebar.types"
import {
  SIDEBAR_TAILWIND_CLASSES,
  SIDEBAR_DEBUG_PATTERNS,
  DEFAULT_CONFIG,
} from "./Sidebar.constants"

/**
 * Componente de área segura del Sidebar
 *
 * @description
 * Área reservada en la parte superior o inferior del sidebar para
 * contenido especial como logos, usuario, configuración, etc.
 *
 * Las áreas seguras:
 * - No contienen items de navegación
 * - Tienen altura porcentual configurable
 * - Pueden mostrar patrones de debug
 * - Son completamente customizables
 *
 * Casos de uso comunes:
 * - Top: Logo, título de la aplicación, búsqueda
 * - Bottom: Usuario, configuración, logout
 *
 * @example
 * ```tsx
 * <SidebarNav>
 *   <SidebarSafeArea position="top" percent={20}>
 *     <img src="/logo.png" alt="Logo" />
 *   </SidebarSafeArea>
 *
 *   <SidebarContent>
 *     <SidebarItem href="/" />
 *   </SidebarContent>
 *
 *   <SidebarSafeArea position="bottom" percent={15}>
 *     <UserProfile />
 *   </SidebarSafeArea>
 * </SidebarNav>
 * ```
 *
 * @example
 * ```tsx
 * // Con estilos custom
 * <SidebarSafeArea
 *   position="top"
 *   className="bg-zinc-950 border-b border-zinc-800"
 * >
 *   <h1>Mi App</h1>
 * </SidebarSafeArea>
 * ```
 *
 * @example
 * ```tsx
 * // Override de porcentaje
 * <SidebarSafeArea position="bottom" percent={30}>
 *   <ExtendedFooter />
 * </SidebarSafeArea>
 * ```
 */
export const SidebarSafeArea = forwardRef<HTMLDivElement, SidebarSafeAreaProps>(
  function SidebarSafeArea(
    { children, position, percent, className = "", style, as, ...props },
    ref,
  ) {
    const context = useSidebarContext()

    // Obtener porcentaje desde props o config
    const percentValue =
      percent ??
      (position === "top"
        ? context.dimensions.collapsedWidth // Este parece un error en el código original, debería ser safeAreas
        : context.dimensions.collapsedWidth) // Voy a usar un valor por defecto razonable

    // En realidad, el código original usa safeAreas.topPercent y bottomPercent
    // Voy a corregir esto
    const actualPercent =
      percent ?? (position === "top" ? DEFAULT_CONFIG.safeAreas.topPercent : DEFAULT_CONFIG.safeAreas.bottomPercent)

    // Elemento a renderizar (default: div)
    const Component = (as || "div") as ElementType

    // Obtener debug mode del contexto
    const { debug } = context

    // Estilos del área segura
    const safeAreaStyles: CSSProperties = {
      height: `${actualPercent}%`,
      ...style,
    }

    // Si está en debug, mostrar patrón visual
    if (debug) {
      return (
        <Component
          ref={ref}
          className={`${SIDEBAR_TAILWIND_CLASSES.debugBorder} ${className}`}
          style={{
            ...safeAreaStyles,
            background:
              "repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(34,197,94,0.1) 10px, rgba(34,197,94,0.1) 20px)",
            borderBottom: position === "top" ? `2px dashed ${SIDEBAR_DEBUG_PATTERNS.borderColor}` : undefined,
            borderTop: position === "bottom" ? `2px dashed ${SIDEBAR_DEBUG_PATTERNS.borderColor}` : undefined,
          }}
          {...props}
        >
          <div className={`${SIDEBAR_TAILWIND_CLASSES.safeArea} relative h-full`}>
            {/* Label de debug superpuesto con mejor contraste */}
            <span className="absolute top-2 left-2 text-[10px] text-emerald-300 font-mono z-50 bg-black/60 px-1.5 py-0.5 rounded">
              SAFE {actualPercent}% ({position.toUpperCase()})
            </span>
            {/* Children visibles con el patrón de fondo */}
            {children}
          </div>
        </Component>
      )
    }

    // Renderizado normal
    return (
      <Component
        ref={ref}
        className={`${SIDEBAR_TAILWIND_CLASSES.safeArea} ${className}`}
        style={safeAreaStyles}
        {...props}
      >
        {children}
      </Component>
    )
  },
)
